import { useEffect, useRef, useState } from "react";
import { Seo, productSchema, breadcrumbSchema, faqSchema } from "@/lib/seo";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CustomBuildModal } from "@/components/CustomBuildModal";
import { collectionLabel } from "@/lib/collections";
import { toast } from "sonner";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Monitor, Tablet, Smartphone, Check, Loader2, ArrowLeft, ArrowUpRight,
  Star, Gauge, Zap, ShieldCheck, Search as SearchIcon, Sparkles, Code2, Layers,
  ArrowRight, Quote, ChevronRight, PlusCircle,
  Download, RefreshCw, LifeBuoy, Users, Rocket, Crown, Target, TrendingUp,
} from "lucide-react";

const frames = {
  desktop: { icon: Monitor, w: "max-w-5xl", label: "Desktop" },
  tablet: { icon: Tablet, w: "max-w-lg", label: "Tablet" },
  mobile: { icon: Smartphone, w: "max-w-[280px]", label: "Mobile" },
};

const TECHS = [
  { icon: Code2, name: "React" },
  { icon: Layers, name: "Tailwind CSS" },
  { icon: Sparkles, name: "Framer Motion" },
  { icon: Smartphone, name: "Responsive" },
  { icon: SearchIcon, name: "SEO Optimized" },
  { icon: ShieldCheck, name: "Secure & Fast" },
];

export default function TemplateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const heroRef = useRef(null);
  const previewRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"], layoutEffect: false });
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const [tpl, setTpl] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [activeImg, setActiveImg] = useState(0);
  const [buying, setBuying] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [rForm, setRForm] = useState({ rating: 5, comment: "" });
  const [rBusy, setRBusy] = useState(false);
  const [buildModal, setBuildModal] = useState(null); // cta source string, or null
  const openBuild = (src) => setBuildModal(src);
  const demoUrl = tpl?.demo_url || tpl?.live_demo_url || tpl?.preview_url || tpl?.live_url || "";
  const viewDemo = () => { if (demoUrl) window.open(demoUrl, "_blank", "noopener"); else previewRef.current?.scrollIntoView({ behavior: "smooth" }); };

  const loadReviews = () => api.get(`/templates/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});

  useEffect(() => {
    window.scrollTo(0, 0);
    setTpl(null);
    api.get(`/templates/${id}`).then((r) => { setTpl(r.data); setActiveImg(0); }).catch(() => setTpl(false));
    loadReviews();
  }, [id]);

  const submitReview = async () => {
    setRBusy(true);
    try {
      await api.post(`/templates/${id}/reviews`, { rating: rForm.rating, comment: rForm.comment });
      toast.success("Thanks for your review!");
      setRForm({ rating: 5, comment: "" });
      loadReviews();
      const { data } = await api.get(`/templates/${id}`); setTpl(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setRBusy(false); }
  };

  const buyTemplate = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/checkout/session", { origin_url: window.location.origin, kind: "template", template_id: tpl.id });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err)); setBuying(false); }
  };

  if (tpl === null) return <div className="flex justify-center py-40 min-h-screen"><Loader2 className="animate-spin text-gold" size={32} /></div>;
  if (tpl === false) return <div data-testid="preview-not-found" className="text-center py-40 text-gray-400 min-h-screen">Design not found. <Link to="/marketplace" className="text-gold">Back to marketplace</Link></div>;

  const gallery = [tpl.preview_image, ...(tpl.gallery || [])].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
  const mx = tpl.metrics || {};
  const metrics = [
    { icon: Gauge, value: mx.performance ?? 98, suffix: "", label: "Performance" },
    { icon: ShieldCheck, value: mx.accessibility ?? 100, suffix: "", label: "Accessibility" },
    { icon: SearchIcon, value: mx.seo ?? 100, suffix: "", label: "SEO" },
    { icon: Zap, value: mx.load_time ?? 0.8, suffix: "s", label: "Load Time", decimal: true },
  ];
  const techs = (tpl.tech_stack && tpl.tech_stack.length) ? tpl.tech_stack.map((name) => ({ icon: Code2, name })) : TECHS;
  const canReview = user && user.role;

  return (
    <div className="bg-navy">
      <Seo
        title={(tpl.seo_title || "").trim() || `${tpl.title} — Website Design | ATF WebWorks`}
        description={(tpl.meta_description || "").trim() || (tpl.description || "").slice(0, 160) || `Professional ${collectionLabel(tpl.category)} website design — customized, launched, hosted, and grown for your business by ATF WebWorks.`}
        path={`/template/${tpl.slug || id}`}
        type="product"
        image={tpl.preview_image}
        jsonLd={[
          productSchema({ name: tpl.title, description: (tpl.meta_description || tpl.description || "").trim(), image: tpl.preview_image, category: tpl.category, slug: tpl.slug || id }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Website Designs", path: "/marketplace" }, { name: tpl.title, path: `/template/${tpl.slug || id}` }]),
          ...(Array.isArray(tpl.faqs) && tpl.faqs.length ? [faqSchema(tpl.faqs)] : []),
        ]}
      />
      {/* FULL-WIDTH HERO */}
      <section ref={heroRef} className="relative h-[88vh] min-h-[600px] overflow-hidden flex items-end">
        <motion.img style={{ scale: heroScale, y: heroY }} src={gallery[0]} alt={`${tpl.title} website design — hero preview`}
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-navy/30" />
        <motion.div style={{ opacity: heroOpacity }} className="relative max-w-7xl mx-auto px-6 md:px-12 pb-20 w-full">
          <Link to="/marketplace" className="inline-flex items-center gap-2 text-gray-300 hover:text-gold mb-8" data-testid="back-to-marketplace"><ArrowLeft size={16} /> Marketplace</Link>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
            <div className="flex items-center gap-4 mb-5">
              <span className="text-xs uppercase tracking-[0.2em] bg-gold text-navy px-3 py-1 font-bold">{collectionLabel(tpl.category)}</span>
              {tpl.reviews > 0 && (
                <span className="flex items-center gap-1 text-white text-sm"><Star size={15} className="fill-gold text-gold" /> {Number(tpl.rating).toFixed(1)} <span className="text-gray-400">({tpl.reviews} {tpl.reviews === 1 ? "review" : "reviews"})</span></span>
              )}
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light text-white max-w-4xl leading-[1.02]">{tpl.title}</h1>
            <p className="text-lg text-gray-200 font-light mt-6 max-w-2xl leading-relaxed">{tpl.description}</p>
            <div className="flex flex-wrap items-center gap-4 mt-10">
              <button onClick={() => openBuild("hero")} data-testid="hero-customize-btn" className="bg-gold text-navy px-8 py-4 font-semibold hover:bg-gold-light transition-colors flex items-center gap-2">
                Build This for My Business <ArrowUpRight size={18} />
              </button>
              <button onClick={viewDemo} data-testid="hero-demo-btn" className="border border-white/30 text-white px-8 py-4 font-medium hover:bg-white/10 transition-colors flex items-center gap-2">
                <Monitor size={18} /> View Live Demo
              </button>
            </div>
            <p className="text-sm text-gray-400 mt-4 max-w-2xl" data-testid="hero-care-nudge">
              Every build can include <button onClick={() => navigate("/marketplace#care-plans")} className="text-gold hover:underline">Hosting &amp; Care from $49/mo</button> — we plan it, build it, launch it, maintain it, secure it, and help it grow.
            </p>
          </motion.div>
        </motion.div>
      </section>

      <TrustBar />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 py-20 lg:py-28">
          {/* LEFT CONTENT */}
          <div className="lg:col-span-8 space-y-28">

            {/* IS THIS RIGHT FOR YOU — outcome framing */}
            <Reveal>
              <WhoFor tpl={tpl} />
            </Reveal>

            {/* OUTCOMES / DELIVERABLES / SUCCESS METRICS (renders only when authored) */}
            {(tpl.outcomes?.length || tpl.deliverables?.length || tpl.success_metrics?.length) ? (
              <Reveal><SolutionValue tpl={tpl} /></Reveal>
            ) : null}

            {/* DEVICE SWITCHER + INTERACTIVE GALLERY */}
            <div ref={previewRef} className="scroll-mt-28" />
            <Reveal>
              <SectionLabel>See it in action</SectionLabel>
              <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight mb-8">Pixel-perfect on every screen</h2>
              <div className="flex items-center gap-3 mb-6" data-testid="device-switcher">
                {Object.entries(frames).map(([key, f]) => (
                  <button key={key} onClick={() => setDevice(key)} data-testid={`device-${key}`}
                    className={`flex items-center gap-2 px-4 py-2 text-sm border transition-colors ${device === key ? "bg-gold text-navy border-gold font-medium" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>
                    <f.icon size={15} /> {f.label}
                  </button>
                ))}
              </div>
              <div className="bg-navy-800 border border-white/10 p-6 md:p-10 flex justify-center">
                <motion.div layout transition={{ type: "spring", stiffness: 200, damping: 26 }} className={`w-full ${frames[device].w}`}>
                  <div className="border border-white/10 overflow-hidden shadow-2xl">
                    <div className="h-8 bg-navy-700 flex items-center gap-1.5 px-3">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                      <span className="w-2.5 h-2.5 rounded-full bg-gold/70" />
                      <span className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
                    </div>
                    <motion.img key={activeImg} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
                      src={gallery[activeImg]} alt={`${tpl.title} website design screenshot`} className="w-full object-cover" />
                  </div>
                </motion.div>
              </div>
              {gallery.length > 1 && (
                <div className="flex gap-3 mt-5 overflow-x-auto pb-2" data-testid="gallery-thumbs">
                  {gallery.map((g, i) => (
                    <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                      className={`relative shrink-0 w-32 h-20 overflow-hidden border-2 transition-colors ${activeImg === i ? "border-gold" : "border-white/10 hover:border-white/30"}`}>
                      <img src={g} alt={`${tpl.title} design screenshot ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </Reveal>

            {/* PERFORMANCE METRICS */}
            <Reveal>
              <div data-testid="customize-design-banner" className="bg-navy-800 border border-gold/25 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-28">
                <div>
                  <div className="text-white font-heading text-lg">Like this design but need something different?</div>
                  <p className="text-gray-400 text-sm mt-1">We use it as a starting point and tailor everything to your business.</p>
                </div>
                <button onClick={() => openBuild("customize")} data-testid="customize-design-btn"
                  className="bg-gold text-navy px-6 py-3 font-semibold hover:bg-gold-light transition-colors flex items-center gap-2 shrink-0">
                  Customize This Design <ArrowUpRight size={16} />
                </button>
              </div>
              <SectionLabel>Performance</SectionLabel>
              <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight mb-3">Built for speed & quality</h2>
              <p className="text-gray-400 mb-10 max-w-xl">Every design ships with production-grade performance, accessibility, and SEO baked in.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {metrics.map((m) => <Metric key={m.label} {...m} />)}
              </div>
            </Reveal>

            {/* TECHNOLOGIES */}
            <Reveal>
              <SectionLabel>Under the hood</SectionLabel>
              <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight mb-8">Technologies used</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="technologies">
                {techs.map((tech, i) => (
                  <motion.div key={tech.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                    className="glass p-5 flex items-center gap-3">
                    <tech.icon className="text-gold" size={20} strokeWidth={1.5} />
                    <span className="text-white font-medium">{tech.name}</span>
                  </motion.div>
                ))}
              </div>
            </Reveal>

            {/* FEATURES */}
            <Reveal>
              <SectionLabel>What's inside</SectionLabel>
              <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight mb-8">Features</h2>
              <div className="grid sm:grid-cols-2 gap-4" data-testid="features-list">
                {tpl.features?.map((f, i) => (
                  <motion.div key={f} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                    className="flex items-center gap-4 bg-navy-800 border border-white/5 p-5">
                    <div className="w-9 h-9 shrink-0 border border-gold/40 flex items-center justify-center text-gold"><Check size={16} /></div>
                    <span className="text-gray-200">{f}</span>
                  </motion.div>
                ))}
              </div>
            </Reveal>

            {/* INCLUDED PAGES */}
            <Reveal>
              <SectionLabel>Complete site</SectionLabel>
              <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight mb-8">Included pages</h2>
              <div className="flex flex-wrap gap-3" data-testid="pages-list">
                {tpl.pages?.map((p, i) => (
                  <motion.span key={p} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                    className="flex items-center gap-2 border border-white/15 text-gray-200 px-4 py-2.5 hover:border-gold/40 transition-colors">
                    <ChevronRight size={14} className="text-gold" /> {p}
                  </motion.span>
                ))}
              </div>
            </Reveal>

            {/* UPGRADE PATH — Growth → Premium → AI Platform */}
            <Reveal>
              <UpgradePath tpl={tpl} onCustomize={() => openBuild("upgrade")} />
            </Reveal>

            {/* WHAT'S INCLUDED WHEN WE BUILD IT */}
            <Reveal>
              <WhatsIncluded />
            </Reveal>

            {/* FAQ */}
            {tpl.faqs?.length > 0 && (
              <Reveal>
                <SectionLabel>Questions</SectionLabel>
                <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight mb-8">Frequently asked</h2>
                <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                  {tpl.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                      <AccordionTrigger className="text-white hover:text-gold text-left text-lg">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-gray-400 leading-relaxed text-base">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            )}

            {/* REVIEWS */}
            <Reveal>
              <SectionLabel>Loved by clients</SectionLabel>
              <div className="flex items-end gap-6 mb-8 flex-wrap">
                <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight">Reviews</h2>
                {reviews.length > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">{Array.from({ length: 5 }).map((_, k) => <Star key={k} size={18} className={k < Math.round(tpl.rating) ? "fill-gold text-gold" : "text-gray-600"} />)}</div>
                    <span className="text-white font-medium">{Number(tpl.rating).toFixed(1)}</span>
                    <span className="text-gray-500">· {reviews.length} {reviews.length === 1 ? "review" : "reviews"}</span>
                  </div>
                )}
              </div>

              {reviews.length > 0 ? (
                <div className="grid md:grid-cols-3 gap-6" data-testid="reviews">
                  {reviews.map((r) => (
                    <motion.div key={r.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass p-6" data-testid={`review-${r.id}`}>
                      <div className="flex gap-0.5 mb-4">{Array.from({ length: 5 }).map((_, k) => <Star key={k} size={13} className={k < r.rating ? "fill-gold text-gold" : "text-gray-600"} />)}</div>
                      {r.comment && <p className="text-gray-200 leading-relaxed mb-5 text-sm">"{r.comment}"</p>}
                      <div className="text-white text-sm font-medium">{r.user_name}</div>
                      <div className="text-gray-500 text-xs">{new Date(r.created_at).toLocaleDateString()}</div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="glass p-8 text-center text-gray-400" data-testid="reviews-empty">
                  <Quote className="text-gold/60 mx-auto mb-3" size={26} />
                  No reviews yet — be the first to share your experience with this design.
                </div>
              )}

              {/* WRITE A REVIEW */}
              <div className="glass p-6 mt-6" data-testid="review-form">
                <h3 className="font-heading text-xl text-white mb-4">Write a review</h3>
                {canReview ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2" data-testid="review-stars">
                      {Array.from({ length: 5 }).map((_, k) => (
                        <button key={k} type="button" onClick={() => setRForm((p) => ({ ...p, rating: k + 1 }))} data-testid={`review-star-${k + 1}`} aria-label={`${k + 1} star`}>
                          <Star size={26} className={k < rForm.rating ? "fill-gold text-gold" : "text-gray-600 hover:text-gold/60"} />
                        </button>
                      ))}
                    </div>
                    <textarea
                      rows={3} value={rForm.comment} onChange={(e) => setRForm((p) => ({ ...p, comment: e.target.value }))}
                      placeholder="Tell others what you thought…" data-testid="review-comment"
                      className="w-full bg-navy border border-white/15 text-white p-3 text-sm focus:border-gold focus:outline-none"
                    />
                    <button onClick={submitReview} disabled={rBusy} data-testid="submit-review-btn"
                      className="bg-gold text-navy px-6 py-2.5 font-semibold hover:bg-gold-light transition-colors flex items-center gap-2 disabled:opacity-60">
                      {rBusy ? <Loader2 className="animate-spin" size={16} /> : "Submit review"}
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm" data-testid="review-signin-prompt">
                    <Link to="/login" className="text-gold hover:text-gold-light">Sign in</Link> to leave a review.
                  </p>
                )}
              </div>
            </Reveal>
          </div>

          {/* STICKY PURCHASE PANEL */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <div className="glass p-8" data-testid="purchase-panel">
                <div className="flex items-center gap-2 text-sm text-white mb-6">
                  {tpl.reviews > 0 ? (
                    <><Star size={15} className="fill-gold text-gold" /> {Number(tpl.rating).toFixed(1)}
                    <span className="text-gray-500">({tpl.reviews})</span></>
                  ) : (
                    <span className="text-gray-400 text-xs uppercase tracking-wide">New design</span>
                  )}
                </div>
                <div className="pb-6 border-b border-white/10">
                  <div className="text-sm text-gray-400">We customize this solution for your business</div>
                  <div className="font-heading text-4xl text-gold">Starting at $997</div>
                  <div className="text-xs text-gray-500 mt-1">Includes customization and launch · Typical build time 1–2 weeks</div>
                </div>
                <button onClick={() => openBuild("panel")} data-testid="customize-btn"
                  className="w-full mt-6 bg-gold text-navy py-4 font-semibold hover:bg-gold-light transition-colors flex items-center justify-center gap-2">
                  Build This for My Business <ArrowUpRight size={16} />
                </button>
                <button onClick={viewDemo} data-testid="panel-demo-btn"
                  className="w-full mt-3 border border-white/25 text-white py-3 font-medium hover:bg-white/5 transition-colors flex items-center justify-center gap-2">
                  <Monitor size={16} /> View Live Demo
                </button>

                <div className="pt-6 mt-6 border-t border-white/10">
                  <div className="text-sm text-white font-medium mb-3">What's included</div>
                  <div className="space-y-2.5">
                    {["Personalized with your branding", "Your content added", "Deployed to your domain", "Training included", "Hosting & Care available"].map((x) => (
                      <div key={x} className="flex items-center gap-3 text-sm text-gray-300"><Check size={15} className="text-gold shrink-0" /> {x}</div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-white/10 flex items-start gap-2.5" data-testid="panel-care-nudge">
                  <ShieldCheck size={16} className="text-gold shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-400 leading-relaxed">Keep it growing after launch with a <button onClick={() => navigate("/marketplace#care-plans")} className="text-gold hover:underline">Hosting &amp; Care plan</button> — from $49/mo.</p>
                </div>

                {tpl.addons?.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-white/10" data-testid="addons-panel">
                    <div className="flex items-center gap-2 mb-1">
                      <PlusCircle size={15} className="text-gold" />
                      <div className="text-sm text-white font-medium">Add-ons available</div>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">Optional extras we can build in during a custom project.</p>
                    <div className="space-y-3">
                      {tpl.addons.map((a) => (
                        <div key={a.name} data-testid={`addon-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm text-gray-200">{a.name}</div>
                            {a.note && <div className="text-[11px] text-gray-500 leading-snug">{a.note}</div>}
                          </div>
                          <div className="text-sm text-gold font-heading whitespace-nowrap">{a.price}</div>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => openBuild("addons")} data-testid="addons-cta"
                      className="w-full mt-5 bg-gold/10 border border-gold/40 text-gold py-2.5 text-sm font-medium hover:bg-gold/20 transition-colors flex items-center justify-center gap-2">
                      Add these to my build <ArrowUpRight size={14} />
                    </button>
                  </div>
                )}

                <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
                  {[["AI Ready", tpl.ai_ready], ["SEO Optimized", tpl.seo_ready], ["Fully Responsive", tpl.responsive]].map(([label, on]) => on && (
                    <div key={label} className="flex items-center gap-3 text-sm text-gray-300"><Check size={15} className="text-gold" /> {label}</div>
                  ))}
                  <div className="flex items-center gap-3 text-sm text-gray-300"><Check size={15} className="text-gold" /> Launched on your domain</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RELATED */}
        {tpl.related?.length > 0 && (
          <Reveal>
            <div className="py-16 border-t border-white/10">
              <SectionLabel>Keep exploring</SectionLabel>
              <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight mb-10">You may also like…</h2>
              <div className="grid md:grid-cols-3 gap-8" data-testid="related-templates">
                {tpl.related.map((r, i) => (
                  <motion.div key={r.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                    <Link to={`/template/${r.slug || r.id}`} className="group block bg-navy-800 border border-white/10 hover:border-gold/50 transition-colors overflow-hidden" data-testid={`related-${r.id}`}>
                      <div className="h-44 overflow-hidden">
                        {r.preview_image ? (
                          <img src={r.preview_image} alt={r.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-navy-700 text-gray-500 text-xs uppercase tracking-wide">{collectionLabel(r.category)}</div>
                        )}
                      </div>
                      <div className="p-5">
                        <div className="text-xs text-gold uppercase tracking-[0.15em] mb-1">{collectionLabel(r.category)}</div>
                        <h3 className="font-heading text-lg text-white group-hover:text-gold transition-colors">{r.title}</h3>
                        <div className="flex items-center justify-end mt-3">
                          <span className="text-gold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">View demo <ArrowRight size={13} /></span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </Reveal>
        )}
      </div>

      {/* MOBILE STICKY BAR */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-navy/95 backdrop-blur-xl border-t border-white/10 p-3 flex gap-3" data-testid="mobile-buy-bar">
        <button onClick={() => openBuild("mobile")} className="flex-1 bg-gold text-navy py-3 font-semibold flex items-center justify-center gap-1.5">
          Build This for My Business <ArrowUpRight size={15} />
        </button>
        <button onClick={viewDemo} className="flex-1 border border-white/25 text-white py-3 font-medium flex items-center justify-center gap-2">
          <Monitor size={16} /> Live Demo
        </button>
      </div>

      <CustomBuildModal open={!!buildModal} onClose={() => setBuildModal(null)}
        template={{ title: tpl.title, slug: tpl.slug || tpl.id, category: tpl.category }} source={buildModal || ""} />
    </div>
  );
}

function SectionLabel({ children }) {
  return <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-4"><span className="h-px w-8 bg-gold" /> {children}</div>;
}

function TrustBar() {
  const items = [
    { icon: Sparkles, label: "Custom-built for your business" },
    { icon: Rocket, label: "Launched on your domain" },
    { icon: SearchIcon, label: "SEO-ready" },
    { icon: Smartphone, label: "Fully responsive" },
    { icon: LifeBuoy, label: "Hosting & Care available" },
  ];
  return (
    <div className="border-y border-white/10 bg-navy-800/60 backdrop-blur-sm" data-testid="trust-bar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
        {items.map((i) => (
          <div key={i.label} className="flex items-center gap-2 text-sm text-gray-300">
            <i.icon size={16} className="text-gold shrink-0" /> {i.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function WhoFor({ tpl }) {
  const cards = [
    { icon: Users, title: "Who it's for", body: tpl.ideal_customer || tpl.target_customer || `${collectionLabel(tpl.category)} organizations that want a professional, launch-ready web presence.` },
    { icon: Target, title: "What it solves", body: tpl.problem_solved || `A credible, conversion-focused ${collectionLabel(tpl.category).toLowerCase()} website — without the cost and delay of building from scratch.` },
    { icon: Rocket, title: "Best for", body: tpl.business_stage ? `${tpl.business_stage} stage.` : "New launches and refreshes that need to look established from day one." },
  ];
  return (
    <div data-testid="who-for">
      <SectionLabel>Is this right for you?</SectionLabel>
      <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight mb-2">More than a design — a head start for your business</h2>
      {tpl.mission && <p className="text-gold/90 text-sm mb-6 flex items-center gap-2" data-testid="product-mission"><Target size={15} /> Mission: {tpl.mission}</p>}
      <div className={tpl.mission ? "grid sm:grid-cols-3 gap-4" : "grid sm:grid-cols-3 gap-4 mt-6"}>
        {cards.map((c) => (
          <div key={c.title} className="bg-navy-800 border border-white/5 p-6 hover:border-gold/30 transition-colors" data-testid={`whofor-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <c.icon size={20} className="text-gold mb-3" strokeWidth={1.5} />
            <div className="text-white font-medium mb-1.5">{c.title}</div>
            <p className="text-gray-400 text-sm leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SolutionValue({ tpl }) {
  return (
    <div data-testid="solution-value" className="space-y-12">
      {tpl.outcomes?.length > 0 && (
        <div data-testid="product-outcomes">
          <SectionLabel>What you'll achieve</SectionLabel>
          <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight mb-8">The outcomes you can expect</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {tpl.outcomes.map((o, i) => (
              <div key={i} className="flex items-start gap-3 bg-navy-800 border border-white/5 p-4">
                <TrendingUp size={16} className="text-gold shrink-0 mt-0.5" />
                <p className="text-gray-300 text-sm leading-relaxed">{o}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {tpl.deliverables?.length > 0 && (
        <div data-testid="product-deliverables">
          <SectionLabel>What you'll get</SectionLabel>
          <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight mb-8">Exactly what's included</h2>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {tpl.deliverables.map((d, i) => (
              <div key={i} className="flex items-start gap-3"><Check size={16} className="text-gold shrink-0 mt-0.5" /><span className="text-gray-300 text-sm">{d}</span></div>
            ))}
          </div>
        </div>
      )}
      {tpl.success_metrics?.length > 0 && (
        <div data-testid="product-success-metrics" className="bg-navy-800 border border-gold/20 p-6">
          <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-4"><Gauge size={14} /> How you'll know it worked</div>
          <div className="flex flex-wrap gap-2">
            {tpl.success_metrics.map((m, i) => (
              <span key={i} className="text-sm text-gray-200 bg-navy border border-white/10 px-3 py-1.5">{m}</span>
            ))}
          </div>
        </div>
      )}
      {tpl.expected_transformation && (
        <div data-testid="product-transformation" className="bg-gradient-to-br from-gold/10 to-transparent border border-gold/30 p-8">
          <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-3"><Sparkles size={14} /> The transformation</div>
          <p className="text-white text-lg sm:text-xl font-heading leading-relaxed">{tpl.expected_transformation}</p>
        </div>
      )}
    </div>
  );
}

function UpgradePath({ tpl, onCustomize }) {
  const tiers = [
    { key: "growth", tier: "Growth", icon: Rocket, price: "From $997", tagline: "We customize & launch it for you",
      points: ["Custom branding & content", "Professional setup", "Launch support"], cta: "Build This for My Business", action: onCustomize, highlight: true },
    { key: "premium", tier: "Premium", icon: Crown, price: "Custom", tagline: "Custom build + marketing & SEO",
      points: ["Everything in Growth", "SEO & content strategy", "Conversion optimization"], cta: "Talk to us", action: onCustomize, highlight: false },
    { key: "ai", tier: "AI Platform", icon: Sparkles, price: "Let's talk", tagline: "Ongoing AI-powered growth",
      points: ["Top Foster content engine", "Continuous improvements", "Growth partner"], cta: "Explore AI Platform", action: onCustomize, highlight: false },
  ];
  return (
    <div data-testid="upgrade-path">
      <SectionLabel>Your growth path</SectionLabel>
      <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight mb-3">Start here — grow when you're ready</h2>
      <p className="text-gray-400 mb-10 max-w-xl">We build your custom site, then grow with you into marketing, SEO and an AI-powered growth partnership as your business scales.</p>
      <div className="grid sm:grid-cols-3 gap-4">
        {tiers.map((t) => (
          <div key={t.key} data-testid={`tier-${t.key}`}
            className={`relative flex flex-col p-6 border transition-all duration-300 ${t.highlight ? "border-gold bg-gold/[0.06] shadow-[0_0_20px_rgba(251,191,36,0.15)]" : "border-white/10 bg-navy-800 hover:border-gold/30"}`}>
            {t.highlight && <span className="absolute -top-2.5 left-6 bg-gold text-navy text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">Most popular</span>}
            <div className="flex items-center gap-2 mb-3"><t.icon size={18} className="text-gold" /><span className="text-white font-heading text-lg">{t.tier}</span></div>
            <div className="font-heading text-2xl text-white mb-1">{t.price}</div>
            <p className="text-gray-500 text-xs mb-4 leading-snug">{t.tagline}</p>
            <ul className="space-y-2 mb-6 flex-1">
              {t.points.map((p) => <li key={p} className="flex items-start gap-2 text-sm text-gray-300"><Check size={14} className="text-gold shrink-0 mt-0.5" /> {p}</li>)}
            </ul>
            <button onClick={t.action} data-testid={`tier-cta-${t.key}`}
              className={`w-full py-2.5 text-sm font-semibold transition-colors flex items-center justify-center gap-1.5 ${t.highlight ? "bg-gold text-navy hover:bg-gold-light" : "border border-white/20 text-white hover:bg-white/5"}`}>
              {t.cta} <ArrowUpRight size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function WhatsIncluded() {
  const included = [
    "Personalized with your branding", "Your colors and logo", "Your content and photos",
    "Mobile responsive", "Contact forms configured", "SEO-ready",
    "Connected to your domain", "Hosting & Care available",
  ];
  const perfectFor = ["New businesses", "Existing businesses needing a redesign", "Companies wanting a modern online presence"];
  return (
    <div data-testid="whats-included">
      <SectionLabel>When we build it for you</SectionLabel>
      <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight mb-8">What's included</h2>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mb-10" data-testid="whats-included-list">
        {included.map((x) => (
          <div key={x} className="flex items-center gap-3"><Check size={16} className="text-gold shrink-0" /><span className="text-gray-300">{x}</span></div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-navy-800 border border-white/5 p-6" data-testid="perfect-for">
          <div className="text-white font-medium mb-3 flex items-center gap-2"><Users size={16} className="text-gold" /> Perfect for</div>
          <ul className="space-y-2">
            {perfectFor.map((p) => <li key={p} className="flex items-start gap-2 text-sm text-gray-300"><ChevronRight size={14} className="text-gold shrink-0 mt-0.5" /> {p}</li>)}
          </ul>
        </div>
        <div className="bg-gradient-to-br from-gold/10 to-transparent border border-gold/30 p-6 flex flex-col justify-center" data-testid="build-timeline">
          <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-2"><Rocket size={14} /> Typical build time</div>
          <div className="font-heading text-3xl text-white">1–2 weeks</div>
          <p className="text-gray-400 text-sm mt-1">From kickoff to launch on your domain.</p>
        </div>
      </div>
    </div>
  );
}

function Reveal({ children }) {
  return (
    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
      {children}
    </motion.div>
  );
}

function Metric({ icon: Icon, value, suffix, label, decimal }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf, start;
    const dur = 1300;
    const tick = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(decimal ? Math.round(value * eased * 10) / 10 : Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, decimal]);
  return (
    <div ref={ref} className="bg-navy-800 border border-white/5 p-6 text-center" data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon className="text-gold mx-auto mb-3" size={22} strokeWidth={1.5} />
      <div className="font-heading text-4xl text-white">{decimal ? n.toFixed(1) : n}{suffix}</div>
      <div className="text-gray-500 text-sm mt-1">{label}</div>
    </div>
  );
}
